'use client';

import React, { type ReactElement, useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

export const AnimatedList = React.memo(
	({ className, children, delay = 2000 }: { className?: string; children: React.ReactNode; delay?: number }) => {
		const [index, setIndex] = useState(0);
		const [currentkey, setCurrentKey] = useState(0);
		const childrenArray = React.Children.toArray(children);

		type Item = ReactElement & { currentKey: number };

		const [itemsToShow, setItemsToShow] = useState<Item[]>([]);

		useEffect(() => {
			const interval = setInterval(
				() => {
					if (index === 0) {
						setItemsToShow(prev => [{ ...(childrenArray[0] as ReactElement), currentKey: currentkey }, ...prev]);
						setIndex(prevIndex => prevIndex + 1);
						setCurrentKey(prev => prev + 1);
					}

					if (index > 0 && index < childrenArray.length) {
						setItemsToShow(prev => [
							{
								...(childrenArray[currentkey % childrenArray.length] as ReactElement),
								currentKey: currentkey,
							},
							...prev,
						]);
						setIndex(prevIndex => prevIndex + 1);
						setCurrentKey(prev => prev + 1);
					}

					if (index >= childrenArray.length) {
						setItemsToShow(prev => prev.slice(0, childrenArray.length - 2));
						setIndex(0);
					}
				},
				index >= childrenArray.length ? 0 : delay,
			);

			return () => clearInterval(interval);
		}, [childrenArray, childrenArray.length, currentkey, delay, index, itemsToShow, setItemsToShow]);

		return (
			<div className={`flex flex-col items-center gap-4 ${className}`}>
				<AnimatePresence>
					{itemsToShow.map(item => (
						<AnimatedListItem key={(item as Item).currentKey}>{item as ReactElement}</AnimatedListItem>
					))}
				</AnimatePresence>
			</div>
		);
	},
);

AnimatedList.displayName = 'AnimatedList';

export function AnimatedListItem({ children }: { children: React.ReactNode }) {
	const animations = {
		initial: { scale: 0, opacity: 0 },
		animate: { scale: 1, opacity: 0.9, originY: 0 },
		exit: { scale: 0, opacity: 0 },
		transition: { type: 'spring', stiffness: 350, damping: 40 },
	};

	return (
		<motion.div {...animations} layout className="mx-auto w-full">
			{children}
		</motion.div>
	);
}
